import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, videos } from '@/mocks/data';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeSlug, setActiveSlug] = useState(currentCategory.slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredVideos = useMemo(() => {
    return videos.filter((v) => v.category === activeSlug);
  }, [activeSlug]);

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: '视频分类', href: '/category/hot' },
          { label: activeCategory.name },
        ]}
      />

      <section className="w-full px-4 md:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">共 {activeCategory.count} 部作品</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-product-shop>
              {filteredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  slug={video.slug}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  categoryName={video.categoryName}
                  views={video.views}
                  date={video.date}
                  rating={video.rating}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-film-line text-3xl text-secondary-400"></i>
              </div>
              <p className="text-sm text-foreground-500">该分类暂无内容，请浏览其他分类</p>
            </div>
          )}
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory.name} - 蜜桃传媒`,
            description: `蜜桃传媒${activeCategory.name}专区，共${activeCategory.count}部精选作品在线免费观看。`,
            url: `https://www.mitaomedia.com/category/${activeCategory.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '蜜桃传媒',
              url: 'https://www.mitaomedia.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.mitaomedia.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '视频分类',
                  item: 'https://www.mitaomedia.com/category/hot',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: activeCategory.name,
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}